import { Injectable } from '@angular/core';
import { StorageService } from '@nha/services';
import { FacetGroup, facetKeys } from '../counting';
import { IFilterInitValue, IFiltersInstance, IFilterSyncValue } from '../interfaces';

export type FilterStorage = { [key: string]: IFilterInitValue[] };

@Injectable({ providedIn: 'root' })
export class FilterStorageService {
	private readonly storageService = new StorageService(true);

	getInitValues(filterInstance: IFiltersInstance): IFilterInitValue[] {
		let initValues = filterInstance.config.initValues;

		if (filterInstance.config.keepSessionState && filterInstance.filterMap !== null) {
			initValues = this.mergeSessionStateInitValues(filterInstance.key, initValues, filterInstance.config.excludeFromSessionState);
		}

		if (filterInstance.config.syncFilterKeys) {
			initValues = this.mergeFilterSyncInitValues(filterInstance.config.syncFilterKeys, initValues);
		}

		initValues = this.excludeClientFacetInitValues(filterInstance, initValues);

		// Can't determine single vs multi-select without filterMap — nothing to deduplicate
		if (!filterInstance.filterMap) {
			return initValues;
		}

		// Deduplicate: single-select keeps only the last value per key; multi-select keeps all
		const singleSelectKeys = new Set(
			Object.entries(filterInstance.filterMap)
				.filter(([, filter]) => !filter.multiple)
				.map(([key]) => key)
		);

		const lastValueByKey: { [key: string]: IFilterInitValue } = {};
		const multiSelectValues: IFilterInitValue[] = [];

		initValues.forEach((iv) => {
			if (singleSelectKeys.has(iv.filterKey)) {
				lastValueByKey[iv.filterKey] = iv;
			} else {
				multiSelectValues.push(iv);
			}
		});

		initValues = [...Object.values(lastValueByKey), ...multiSelectValues];

		return initValues;
	}

	public getFilter(key: string): IFiltersInstance | null {
		return this.storageService.get<IFiltersInstance>(key);
	}

	public hasFilter(key: string): boolean {
		return this.storageService.hasKey(key);
	}

	public removeFilter(key: string): void {
		this.storageService.remove(key);
	}

	public setFilter(key: string, filterInstance: IFiltersInstance): void {
		this.storageService.set(key, this.withoutExcludedKeys(filterInstance));
	}

	/**
	 * Returns a copy of the instance with its config's `excludeFromSessionState` keys removed from
	 * the filterMap. Both the persisted write and the in-memory restore in
	 * `FilterStateService.addFilterInstance` go through this: a key excluded from session state must
	 * not survive on EITHER path. Scrubbing only the persisted copy leaves the restored in-memory
	 * map holding the key, which is enough to render a chip for it and to post it to `<base>/update`
	 * — and the API looks incoming keys up with a dictionary indexer, so an unknown key is a 500
	 * rather than an ignored key.
	 */
	public withoutExcludedKeys(filterInstance: IFiltersInstance): IFiltersInstance {
		const excludeKeys = filterInstance.config?.excludeFromSessionState;
		if (!excludeKeys?.length || !filterInstance.filterMap) {
			return filterInstance;
		}

		const filtered = { ...filterInstance, filterMap: { ...filterInstance.filterMap } };
		for (const excludeKey of excludeKeys) {
			delete filtered.filterMap[excludeKey];
		}

		return filtered;
	}

	public setSyncedFilterValue(syncValue: IFilterSyncValue): void {
		if (!syncValue?.syncKey) {
			return;
		}

		this.storageService.set<IFilterSyncValue>(`fsk-${syncValue.syncKey}`, syncValue);
	}

	/**
	 * A filter registered as a client-side facet is evaluated in the browser, so it must never
	 * narrow what the server returns — init included, where a selection persisted to session state
	 * would otherwise be replayed as an init value and come back as a server-narrowed set.
	 *
	 * A persisted selection is not lost: handleFilterResponse re-imposes it onto the response map.
	 * That re-imposition is guarded by `if (filtersInstance.filterMap)`, though, so it does NOT
	 * cover a first-ever mount — a config-supplied `initValues` entry for a facet key is dropped
	 * outright and the filter renders unselected. No adopter sets initValues for a facet key today.
	 * Fixing it means excluding only the session-state-derived values here, or re-imposing config
	 * initValues after the response.
	 */
	private excludeClientFacetInitValues(filterInstance: IFiltersInstance, initValues: IFilterInitValue[]): IFilterInitValue[] {
		const groups = filterInstance.config.clientFacets?.groups;
		if (!groups?.length) {
			return initValues;
		}

		const clientSideKeys = new Set(groups.flatMap((group) => facetKeys(group as FacetGroup<unknown>)));

		return initValues.filter((iv) => !clientSideKeys.has(iv.filterKey));
	}

	private mergeSessionStateInitValues(filterInstanceKey: string, initValues: IFilterInitValue[], excludeKeys?: string[]): IFilterInitValue[] {
		let newMergedInitValues: IFilterInitValue[] = [...initValues];
		const filtersSessionState: IFiltersInstance = this.storageService.get<IFiltersInstance>(filterInstanceKey);

		if (!filtersSessionState?.filterMap) {
			return newMergedInitValues;
		}

		Object.keys(filtersSessionState.filterMap).forEach((key) => {
			if (excludeKeys?.includes(key)) {
				return;
			}

			newMergedInitValues = newMergedInitValues.filter((iv) => iv.filterKey !== key);
			const f = filtersSessionState.filterMap[key];
			const initValue = f.items
				?.filter((i) => i.selected)
				.map((i) => {
					return { filterKey: key, value: i.id, hidden: f.hidden };
				});

			if (initValue) {
				newMergedInitValues.push(...initValue);
			}
		});

		return newMergedInitValues;
	}

	private mergeFilterSyncInitValues(syncFilterKeys: string[], initValues: IFilterInitValue[]): IFilterInitValue[] {
		let newMergedInitValues: IFilterInitValue[] = [...initValues];

		const filterSyncValues = this.getAllFilterSyncValues();

		filterSyncValues.forEach((fsk) => {
			if (syncFilterKeys.includes(fsk.syncKey)) {
				newMergedInitValues = newMergedInitValues.filter((iv) => iv.filterKey !== fsk.filterKey);

				// Add synced values. Must be a null/undefined check, not truthiness — 0 is a
				// valid school id (Service Center), and a truthy check here would silently
				// drop it instead of restoring it.
				if (fsk.value !== null && fsk.value !== undefined) {
					if (Array.isArray(fsk.value)) {
						fsk.value.forEach((v) => {
							newMergedInitValues.push({ filterKey: fsk.filterKey, value: v, hidden: false });
						});
					} else {
						newMergedInitValues.push({ filterKey: fsk.filterKey, value: fsk.value, hidden: false });
					}
				}
			}
		});

		return newMergedInitValues;
	}

	private getAllFilterSyncValues(): IFilterSyncValue[] {
		const storeKeys = this.storageService.getKeys().filter((k) => k.startsWith('fsk-'));
		return storeKeys.map((storeKey) => this.storageService.get<IFilterSyncValue>(storeKey));
	}
}
